// /linux page: saved machines, Console / Screen / Files / Machine tabs, and the two engines
// (v86 for everyone, CheerpX when enabled). Runs in its own document (an internal Re:Vault tab),
// so it never touches the start-page bundle, and it never imports ads.
//
// Lifecycle notes (see docs/linux/README.md):
//   - one running machine per page; a Web Lock stops two tabs running the same machine;
//   - the app tells us when our tab is hidden ("revault-frame-visible") so v86 can pause;
//   - pagehide tears everything down (no async saves can finish there);
//   - a CheerpX disk reset is done by reloading this page and deleting BEFORE CheerpX loads.
import { get, subscribe as onSettings } from "../shared/settings-store.js";
import { safeLinuxWispUrl, unconfirmedRelayHost, LINUX_RELAY_OK_KEY } from "../shared/settings-schema.js";
import * as store from "../shared/linux-store.js";
import { LINUX_IMAGES, catalogImage, CHEERPX_RUNTIME_URL } from "../shared/linux-catalog.js";
import { STATE_LABEL, LinuxError, fmtBytes } from "./engine.js";
import * as profiles from "./profiles.js";
import { createTerminal } from "./terminal.js";
import { V86Engine, GUEST_MOUNT_COMMAND, GUEST_SHARE_MOUNT } from "./v86/adapter.js";
import { defaultWispUrl } from "./v86/network.js";
import { CheerpxEngine, requestReset, consumePendingReset } from "./cheerpx/adapter.js";
import { EXPORT_HELPER } from "./cheerpx/export-helper.js";
import { NOTICE_HEADING, NOTICE_TEXT, NOTICE_CHECKBOX, NOTICE_LINKS, ATTRIBUTION, readAcceptance, writeAcceptance } from "./cheerpx/notice.js";
import { h, btn, saveBlob, modal, confirmDialog, copyText } from "./ui.js";
import { promptWatcher, createGuestAssistant } from "./guest.js";

const TUX = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2.5"/><path d="M7 9l3 3-3 3"/><line x1="12" y1="15" x2="17" y2="15"/></svg>';
const AUTOBOOT_KEY = "revault:linux-autoboot"; // sessionStorage, set by the app right before it opens us
const RESTART_KEY = "revault:linux-restart"; // sessionStorage: start this CheerpX machine after a reload
let cheerpxUsed = false; // a CheerpX runtime existed in this document (its workers can outlive delete())
const AUTOBOOT_TTL = 30000;
const BUSY = new Set(["checking", "loading-runtime", "opening-disk", "booting", "stopping"]);

const root = document.getElementById("rv-lx");
// The app posts our in-app tab's visibility; v86 pauses while hidden. Registered before anything
// async so an early "hidden" isn't lost, and seeded from our frame element (same origin).
let initialVisible = true;
try { initialVisible = !(window.frameElement && window.frameElement.hidden); } catch (e) {}
window.addEventListener("message", (e) => {
	if (e.origin !== location.origin || !e.data || e.data.type !== "revault-frame-visible") return;
	S.tabVisible = !!e.data.visible;
	if (S.engine && S.engine.setTabVisible) S.engine.setTabVisible(S.tabVisible);
	if (els.status) renderBar();
});
const S = {
	machines: [],
	activeId: null,
	tab: "console",
	engine: null, // the engine of the machine currently started in this page (running or failed)
	engineId: null,
	starting: null, // AbortController of an in-flight start
	progress: null,
	notice: null,
	exports: null,
	uploads: [],
	tabVisible: initialVisible,
	kms: null,
	guest: null, // v86 auto-setup watcher
	guestReady: false, // v86: the guest reached a login/shell prompt (engine "running" only means the CPU runs)
	disposed: false,
};

// --- engine plumbing -------------------------------------------------------------------------

const cheerpxEnabled = () => get("linuxCheerpx") !== false;
const networkEnabled = () => get("linuxNetwork") !== false;
const activeMachine = () => S.machines.find((m) => m.id === S.activeId) || null;
const engineState = () => (S.engine ? S.engine.state : "idle");
const isRunning = () => !!S.engine && (engineState() === "running" || BUSY.has(engineState()));
const runningMachine = () => (S.engine && isRunning() ? S.machines.find((m) => m.id === S.engineId) || null : null);

function visibleMachines() {
	return S.machines.filter((m) => m.engine === "v86" || cheerpxEnabled() || m.id === S.engineId);
}

// A custom Wisp endpoint from Settings: this origin's own relay, or another one the user confirmed
// in Settings on this device (settings-schema.js safeLinuxWispUrl). A value that arrived any other
// way (an imported backup, a page writing the stored settings) is ignored: Re:Vault's relay is used.
function relayConfirmed() {
	try { return localStorage.getItem(LINUX_RELAY_OK_KEY) || ""; } catch { return ""; }
}
function relaySetting() {
	return safeLinuxWispUrl(get("linuxWispUrl"), relayConfirmed(), location.host);
}
// A saved relay that is not in use because it was never confirmed on this device (for example one
// saved before confirmation existed, or one from a backup): named, so it is never silently dropped.
function relayUnconfirmed() {
	return unconfirmedRelayHost(get("linuxWispUrl"), relayConfirmed(), location.host);
}
function networkNote() {
	if (!networkEnabled()) return "Turned off in Settings > Advanced > Linux machines";
	if (relayOverride()) return "Through a custom relay: " + relayOverride() + " (Settings > Advanced > Linux machines)";
	if (relayUnconfirmed()) return "Through Re:Vault's network relay. The saved relay " + relayUnconfirmed() + " is not used until you confirm it in Settings > Advanced > Linux machines.";
	return "Through Re:Vault's network relay";
}
// Shown by host so it's never hidden.
function relayOverride() {
	const v = relaySetting();
	try { return v ? new URL(v).host : ""; } catch { return ""; }
}

function wispUrl() {
	if (!networkEnabled()) return "";
	return relaySetting() || defaultWispUrl(__RVM.linuxWisp, location);
}

function makeV86() {
	return new V86Engine({
		urls: { script: __RVM.v86, wasm: __RVM.v86Wasm, wasmFallback: __RVM.v86WasmFallback, bios: __RVM.seabios, vgaBios: __RVM.vgabios },
		imagePrefix: __RVM.linux,
		wispUrl: wispUrl(),
		dohUrl: __RVM.linux ? location.origin + __RVM.linux + "q" : "",
		secure: location.protocol === "https:",
		store,
		estimate: store.estimate,
	});
}

function makeCheerpx(machine) {
	let display;
	if (machine.desktop) {
		const canvas = h("canvas", { class: "rv-lx-kms", tabindex: "0", "aria-label": "Linux graphical display" });
		S.kms = canvas;
		display = {
			canvas,
			onActivate: (vt) => {
				if (vt === 7) { setTab("screen"); canvas.focus(); }
				else if (S.tab === "screen") setTab("console");
			},
		};
	}
	return new CheerpxEngine({ runtimeUrl: CHEERPX_RUNTIME_URL, accepted: !!readAcceptance(), display });
}

async function disposeEngine() {
	const e = S.engine;
	S.engine = null; S.engineId = null; S.kms = null; S.guest = null;
	if (term) term.lock(false);
	if (e) { try { await e.dispose(); } catch (err) {} }
}

async function startMachine(machine) {
	if (!machine || S.starting || isRunning()) return;
	if (machine.engine === "cheerpx") {
		if (!cheerpxEnabled()) return showNotice("CheerpX machines are turned off in Settings > Advanced > Linux machines.", "error");
		if (!readAcceptance()) {
			if (!(await licenceNotice())) return;
			writeAcceptance(true);
		}
		// CheerpX needs a cross-origin isolated page. A Linux tab inside a shell that is not isolated
		// (the ad-capable start page on an approved site, docs/ADS-PLUMBING.md 1.2) cannot be, so
		// offer its own window: a top-level /linux always keeps the isolation headers.
		if (globalThis.crossOriginIsolated !== true && window.top !== window) return showOwnWindow();
		// One CheerpX runtime per document: a second start (restart, or another CheerpX machine)
		// reloads the tab first so no stale runtime worker can touch the disk (and its ~700 MB goes).
		if (cheerpxUsed) {
			try { sessionStorage.setItem(RESTART_KEY, JSON.stringify({ id: machine.id, at: Date.now() })); } catch (e) {}
			location.reload();
			return;
		}
		cheerpxUsed = true;
	}
	hideNotice();
	await disposeEngine();
	S.exports = null;
	S.engineId = machine.id;
	S.engine = machine.engine === "v86" ? makeV86() : makeCheerpx(machine);
	const engine = S.engine;
	engine.onState(() => {
		renderBar(); renderSidebar();
		if (S.tab === "machine") renderMachine(); // lock/unlock the form as the machine starts/stops
		if (S.tab === "files") { if (engine.state === "running") refreshExports(); else renderFiles(); }
	});
	if (engine.onProgress) engine.onProgress((p) => { S.progress = p; renderProgress(); });
	if (engine.onDisplay) engine.onDisplay(() => fitScreen());
	if (engine.onNetwork) engine.onNetwork(() => renderBar());
	engine.onActivity(() => renderBar());
	mountScreen();
	term.clear();
	const resuming = machine.engine === "v86" && (await hasSnapshot(machine));
	term.writeln(resuming ? "Re:Vault Linux · " + machine.name + ": resuming from its saved state…" : banner(machine));
	if (engine.capabilities.id === "cheerpx") { term.fit(); term.lock(true); } // console size is fixed at start
	armGuest(machine, !resuming);
	const consoleSink = {
		write: (b) => {
			term.write(b);
			if (S.guest) S.guest.feed(b);
			if (S.readyWatch && !S.guestReady && S.readyWatch(b)) { S.guestReady = true; renderBar(); renderSidebar(); }
		},
		get cols() { return term.cols; }, get rows() { return term.rows; },
	};
	S.starting = new AbortController();
	S.progress = { stage: "start" };
	renderBar(); renderProgress();
	if (machine.persist) { try { navigator.storage && navigator.storage.persist && navigator.storage.persist(); } catch (e) {} }
	try {
		await engine.start(machine, consoleSink, S.starting.signal);
		if (S.engine !== engine || S.disposed) return;
		if (engine.setTabVisible) engine.setTabVisible(S.tabVisible);
		if (machine.desktop) setTab("screen"); else if (S.tab !== "screen" && mayTakeFocus()) term.focus();
		fitScreen();
	} catch (err) {
		if (S.engine !== engine || S.disposed) return;
		if (err && err.code === "ABORTED") return;
		showNotice(err && err.message ? err.message : String(err), "error");
	} finally {
		if (S.engine === engine) { S.starting = null; S.progress = null; renderProgress(); renderBar(); renderSidebar(); }
	}
}

async function stopMachine() {
	const engine = S.engine;
	if (!engine) return;
	if (S.starting) { S.starting.abort(); return; }
	try {
		if (engine.capabilities.id === "cheerpx" && engine.state === "running") {
			const m = runningMachine();
			if (m && m.persist) { showNotice("Saving the disk before stopping…"); await engine.flush(); }
		}
		await engine.stop();
		if (engine.capabilities.id === "cheerpx") term.lock(false);
		const m = S.machines.find((x) => x.id === S.engineId);
		renderUsage();
		showNotice(m && m.persist ? (engine.capabilities.id === "v86" ? "Stopped and saved. Start it again to pick up where you left off." : "Disk saved and machine stopped.") : "Stopped.", "ok");
	} catch (err) {
		showNotice(err && err.message ? err.message : String(err), "error");
	}
	renderBar(); renderSidebar();
}

async function saveNow() {
	const engine = S.engine;
	if (!engine || engine.state !== "running") return;
	try {
		if (engine.capabilities.id === "v86") {
			showNotice("Saving machine state…");
			const r = await engine.saveSnapshot();
			showNotice("Saved (" + fmtBytes(r.bytes) + "). Starting this machine later resumes exactly here.", "ok");
			renderUsage();
		} else {
			showNotice("Saving disk… (pause anything that is writing files)");
			await engine.flush();
			showNotice("Disk saved. It is kept in this browser; running programs are not. Download important files as a backup too.", "ok");
		}
	} catch (err) {
		showNotice(err && err.message ? err.message : String(err), "error");
	}
}

async function hasSnapshot(machine) {
	if (!machine.persist) return false;
	try { return !!(await store.getSnapshot(machine.id)); } catch (e) { return false; }
}

function banner(m) {
	if (m.engine === "cheerpx") return "Re:Vault Linux · " + m.name + " (CheerpX)\nStarting… the disk streams from Leaning Technologies (first start can take a minute).";
	const img = m.image.startsWith("catalog:") ? catalogImage(m.image.slice(8)) : null;
	return "Re:Vault Linux · " + m.name + " (v86, " + m.memory + " MB)\n" + (img ? img.name + ": log in as root, no password." : "Booting…");
}

// Readiness + one-click setup watchers for a v86 boot. `fresh` = a real boot (not a restored
// snapshot, which is already logged in); also re-armed when the machine is rebooted.
function armGuest(machine, fresh) {
	const v86 = machine.engine === "v86";
	S.guestReady = !v86 || !fresh;
	S.readyWatch = v86 && fresh ? promptWatcher() : null;
	S.guest = v86 && fresh && machine.autologin ? guestAssistant(machine) : null;
}

// Fresh Alpine boot: the one-click setup (see guest.js). Only on a fresh boot of the pinned image:
// a restored snapshot is already logged in.
function guestAssistant(machine) {
	const send = (text) => { if (S.engine && S.engine.state === "running") S.engine.sendInput(new TextEncoder().encode(text)); };
	return createGuestAssistant({ alpine: machine.image.startsWith("catalog:alpine-"), network: networkEnabled() && machine.network !== false, mountCommand: GUEST_MOUNT_COMMAND, sharePath: GUEST_SHARE_MOUNT, send, size: () => ({ cols: term.cols, rows: term.rows }) });
}

// --- UI -----------------------------------------------------------------------------------

let term = null;
const els = {};

function build() {
	root.textContent = "";
	root.removeAttribute("aria-busy");
	els.list = h("ul", { class: "rv-lx-machines", "aria-label": "Machines" });
	els.usage = h("span");
	const side = h("aside", { class: "rv-lx-side" },
		h("div", { class: "rv-lx-side-head" }, h("h1", { html: TUX + "<span>Linux</span>" }), btn("New", newMachineDialog, "", { title: "New machine", "aria-label": "New machine" })),
		els.list,
		h("div", { class: "rv-lx-side-foot" },
			els.usage,
			h("span", null, h("a", { href: "/help#linux", onclick: (e) => { e.preventDefault(); openApp("/help"); } }, "Help"), " · ", h("a", { href: "/settings", onclick: (e) => { e.preventDefault(); openApp("/settings"); } }, "Settings"), " · ", h("a", { href: "/licenses/linux/", onclick: (e) => { e.preventDefault(); licencesDialog(); } }, "Licences"))
		)
	);
	els.title = h("b");
	els.sub = h("span");
	els.status = h("div", { class: "rv-lx-status", role: "status", "aria-live": "polite" });
	els.actions = h("div", { class: "rv-lx-actions" });
	els.progress = h("div", { class: "rv-lx-progress", hidden: true }, h("span"), h("div", { class: "rv-lx-progress-track" }, h("div", { class: "rv-lx-progress-fill" })));
	els.notice = h("div", { class: "rv-lx-notice", role: "alert", hidden: true }, h("p"), h("button", { type: "button", "aria-label": "Dismiss", onclick: hideNotice }, "×"));
	els.tabs = h("nav", { class: "rv-lx-tabs", role: "tablist" });
	els.panels = {};
	for (const [id, label] of [["console", "Console"], ["screen", "Screen"], ["files", "Files"], ["machine", "Machine"]]) {
		els.tabs.append(h("button", { type: "button", role: "tab", class: "rv-lx-tab", id: "rv-lx-tab-" + id, "aria-controls": "rv-lx-panel-" + id, "data-tab": id, onclick: () => setTab(id) }, label));
		els.panels[id] = h("section", { class: "rv-lx-panel", role: "tabpanel", id: "rv-lx-panel-" + id, "aria-labelledby": "rv-lx-tab-" + id, hidden: true });
	}
	const main = h("main", { class: "rv-lx-main" },
		h("header", { class: "rv-lx-bar" }, h("div", { class: "rv-lx-title" }, els.title, els.sub), els.status, els.actions),
		els.progress, els.notice, els.tabs, ...Object.values(els.panels)
	);
	root.append(side, main);

	// Console: one terminal for the page, reused across machines.
	const consoleHost = h("div", { class: "rv-lx-console" });
	const consoleHint = h("p", { class: "rv-lx-hint", id: "rv-lx-console-hint", text: "Keyboard: press Esc, then Tab, to leave the terminal." });
	els.panels.console.append(consoleHost, consoleHint);
	term = createTerminal(consoleHost);
	const helper = consoleHost.querySelector(".xterm-helper-textarea");
	if (helper) { helper.setAttribute("aria-label", "Terminal input"); helper.setAttribute("aria-describedby", consoleHint.id); }
	term.onData((bytes) => { if (S.engine && S.engine.state === "running") S.engine.sendInput(bytes); });
	term.onBinary((bytes) => { if (S.engine && S.engine.state === "running") S.engine.sendInput(bytes); });

	// Screen: v86 VGA output or the CheerpX desktop canvas. Keyboard/mouse only after a click.
	els.screen = h("div", { class: "rv-lx-screen", tabindex: "0", "aria-label": "Machine screen. Click to control it; Ctrl+Alt+G releases." });
	els.capture = h("div", { class: "rv-lx-capture", hidden: true });
	els.panels.screen.append(els.screen, els.capture);
	els.screen.addEventListener("mousedown", () => captureScreen(true));
	els.screen.addEventListener("blur", () => captureScreen(false));
	document.addEventListener("keydown", (e) => {
		if (e.ctrlKey && e.altKey && (e.key === "g" || e.key === "G")) {
			e.preventDefault(); captureScreen(false); els.screen.blur();
			// Also lets the keyboard out of the terminal.
			const a = document.activeElement;
			if (a && a.closest && a.closest(".rv-lx-console")) { const t = document.getElementById("rv-lx-tab-console"); if (t) t.focus(); }
		}
	}, true);
	if (typeof ResizeObserver !== "undefined") new ResizeObserver(() => fitScreen()).observe(els.screen);
}

// A boot finishes seconds after the click, so only grab the keyboard if the user is still here:
// this page has focus, or the app has nothing else focused (not the palette, the address bar...).
function mayTakeFocus() {
	if (!S.tabVisible) return false;
	if (document.hasFocus()) return true;
	try { const pd = window.parent.document, a = pd.activeElement; return !a || a === pd.body || a === window.frameElement; }
	catch (e) { return false; }
}

function openApp(url) {
	// Internal pages ask the app to open links as tabs (fixed allowlist on the app side).
	try { if (window.parent !== window) { window.parent.postMessage({ type: "revault-open", url }, location.origin); return; } } catch (e) {}
	location.href = url;
}

function setTab(id) {
	S.tab = id;
	for (const b of els.tabs.children) b.setAttribute("aria-selected", String(b.dataset.tab === id));
	for (const [k, p] of Object.entries(els.panels)) p.hidden = k !== id;
	if (id !== "screen") captureScreen(false);
	if (id === "console") { term.fit(); if (mayTakeFocus()) term.focus(); }
	if (id === "screen") { mountScreen(); fitScreen(); }
	if (id === "files") renderFiles();
	if (id === "machine") renderMachine();
}

function mountScreen() {
	const e = S.engine;
	let node = null;
	if (e && e.capabilities.id === "v86") node = e.screen;
	else if (e && S.kms) node = S.kms;
	if (node) { if (node.parentNode !== els.screen) els.screen.replaceChildren(node); return; }
	const m = activeMachine();
	const text = !m ? "No machine selected."
		: m.engine === "cheerpx" && !m.desktop ? "This machine is a terminal: use the Console tab. Choose \"Alpine desktop\" for a graphical machine."
		: "Start the machine to see its screen here.";
	els.screen.replaceChildren(h("p", { class: "rv-lx-screen-empty", text }));
}

function fitScreen() {
	const e = S.engine;
	if (!e || S.tab !== "screen") return;
	if (e.capabilities.id === "v86" && e.state === "running") {
		const r = els.screen.getBoundingClientRect();
		e.fitScreen(r.width - 16, r.height - 16);
	}
}

function captureScreen(on) {
	const e = S.engine;
	if (on && (!e || e.state !== "running" || S.tab !== "screen")) return;
	if (e && e.capabilities.id === "v86") e.setCapture(on);
	if (on && S.kms) S.kms.focus();
	els.capture.hidden = !on;
	if (on) els.capture.replaceChildren("Keyboard and mouse go to the machine · ", h("kbd", { text: "Ctrl" }), "+", h("kbd", { text: "Alt" }), "+", h("kbd", { text: "G" }), " releases");
}

function showNotice(text, kind = "info") {
	S.notice = { text, kind };
	els.notice.hidden = false;
	els.notice.dataset.kind = kind;
	els.notice.firstChild.textContent = text;
}
function hideNotice() { S.notice = null; els.notice.hidden = true; }
function showOwnWindow() {
	showNotice("CheerpX machines need their own window here. Open Linux in its own window, then press Start there. v86 machines keep working in this tab.", "info");
	const a = h("a", { class: "rv-lx-ownwin", href: "/linux", target: "_blank", rel: "noopener" }, "Open in its own window");
	els.notice.firstChild.append(" ", a);
}

function renderSidebar() {
	els.list.textContent = "";
	for (const m of visibleMachines()) {
		const es = m.id === S.engineId && S.engine ? S.engine.state : "idle";
		const st = es === "error" ? "error" : es === "running" ? (S.guestReady ? "running" : "busy") : BUSY.has(es) ? "busy" : "idle";
		const sub = m.engine === "v86" ? "v86 · " + m.memory + " MB" : m.desktop ? "CheerpX · desktop" : "CheerpX · terminal";
		els.list.append(h("li", null, h("button", {
			type: "button", class: "rv-lx-machine", "aria-current": String(m.id === S.activeId), "data-state": st,
			onclick: () => selectMachine(m.id),
		}, h("span", { class: "rv-lx-machine-dot" }), h("span", { class: "rv-lx-machine-name", text: m.name }), h("span", { class: "rv-lx-machine-sub", text: sub }))));
	}
	if (!visibleMachines().length) els.list.append(h("li", { class: "rv-lx-empty", text: "No machines. Press New to add one." }));
}

function renderBar() {
	const m = activeMachine();
	const e = m && m.id === S.engineId ? S.engine : null;
	const state = e ? e.state : "idle";
	els.title.textContent = m ? m.name : "Linux";
	els.sub.textContent = !m ? "" : m.engine === "v86"
		? "v86 · " + (m.image.startsWith("catalog:") ? (catalogImage(m.image.slice(8)) || {}).name || "" : m.image ? "your image" : "no disk") + " · " + m.memory + " MB" + (m.network !== false && networkEnabled() ? (relayOverride() ? " · network via " + relayOverride() : relayUnconfirmed() ? " · network (saved relay not confirmed)" : " · network") : "")
		: "CheerpX · " + (m.desktop ? "Alpine desktop" : "Debian terminal") + (e && e.network && e.network.state === "connected" ? " · Tailscale " + (e.network.ip || "") : "");
	const booting = state === "running" && e && e.capabilities.id === "v86" && !S.guestReady;
	const label = state === "running" && e && e.capabilities.id === "v86" && e.suspended ? "Paused (tab hidden)" : booting ? "Booting" : STATE_LABEL[state] || state;
	els.status.textContent = label;
	els.status.dataset.state = booting ? "busy" : state === "running" ? "running" : state === "error" ? "error" : BUSY.has(state) ? "busy" : "idle";
	const other = runningMachine() && runningMachine().id !== (m && m.id);
	const acts = [];
	if (m && (!e || ["idle", "stopped", "error"].includes(state))) {
		acts.push(btn(state === "stopped" || (e && state === "error") ? "Start again" : "Start", () => startMachine(m), "rv-btn-primary", other ? { disabled: true, title: "Stop " + runningMachine().name + " first" } : {}));
	}
	if (e && (state === "running" || BUSY.has(state))) {
		if (state === "running" && m.persist) acts.push(btn(e.capabilities.id === "v86" ? "Save" : "Save disk", saveNow, "", { title: e.capabilities.id === "v86" ? "Snapshot the whole machine now" : "Write disk changes to this browser" }));
		if (state === "running") acts.push(btn("Restart", restartMachine, "", { title: "Reboot the machine" }));
		acts.push(btn(S.starting ? "Cancel" : "Stop", stopMachine, "", { title: m.persist ? "Save and stop" : "Stop" }));
		if (state === "running" && e.network && e.network.login && m.engine === "cheerpx") {
			acts.push(e.network.state === "login-ready" && e.network.loginUrl
				? h("a", { class: "rv-btn", href: e.network.loginUrl, target: "_blank", rel: "noopener noreferrer" }, "Open Tailscale login")
				: btn(e.network.state === "connected" ? "Tailscale on" : "Connect Tailscale", () => e.network.login().catch((err) => showNotice(err.message, "error")), "", { disabled: e.network.state === "connected" || e.network.state === "connecting" }));
		}
	}
	els.actions.replaceChildren(...acts);
	if (e && e.error && state === "error" && !S.notice) showNotice(e.error.message, "error");
}

function renderProgress() {
	const p = S.progress;
	const e = S.engine;
	const busy = !!p && !!e && BUSY.has(e.state);
	els.progress.hidden = !busy;
	if (!busy) return;
	let text = STATE_LABEL[e.state] + "…", frac = null;
	if (p.stage === "image" && !p.ready) { text = "Preparing the disk on Re:Vault's server… " + fmtBytes(p.loaded) + " of " + fmtBytes(p.total); frac = p.total ? p.loaded / p.total : null; }
	else if (p.stage === "runtime" && p.total) { text = "Loading the emulator… " + Math.round((p.loaded / p.total) * 100) + "%"; frac = p.loaded / p.total; }
	else if (e.state === "loading-runtime" && e.capabilities.id === "cheerpx") text = "Loading CheerpX from Leaning Technologies…";
	else if (e.state === "opening-disk" && e.capabilities.id === "cheerpx") text = "Opening the disk (streamed from disks.webvm.io)…";
	els.progress.firstChild.textContent = text;
	els.progress.dataset.indeterminate = frac == null ? "1" : "0";
	els.progress.querySelector(".rv-lx-progress-fill").style.width = frac == null ? "" : Math.round(frac * 100) + "%";
}

async function selectMachine(id) {
	if (S.activeId === id) return;
	S.activeId = id;
	profiles.setActiveId(id);
	S.exports = null;
	renderSidebar(); renderBar();
	if (!isRunning()) mountScreen();
	if (S.tab === "files") renderFiles();
	if (S.tab === "machine") renderMachine();
}

async function restartMachine() {
	const e = S.engine;
	if (!e || e.state !== "running") return;
	if (e.capabilities.id === "v86") { const m = runningMachine(); if (m) armGuest(m, true); e.reboot(); renderBar(); showNotice("Rebooting…"); return; }
	const m = runningMachine();
	if (!(await confirmDialog("Restart " + m.name + "?", "The disk is saved first, then the machine starts again. Running programs are closed.", "Restart"))) return;
	await stopMachine();
	if (!isRunning()) startMachine(m);
}

// --- Files tab ------------------------------------------------------------------------------

function renderFiles() {
	const p = els.panels.files;
	const m = activeMachine();
	const e = m && m.id === S.engineId ? S.engine : null;
	const running = !!e && e.state === "running";
	const v86 = !m || m.engine === "v86";
	const input = h("input", { type: "file", multiple: true, hidden: true, onchange: () => { sendFiles([...input.files]); input.value = ""; } });
	const drop = h("div", { class: "rv-lx-drop" },
		h("span", { text: running ? "Drop files here to send them to " + m.name : "Start the machine to send it files." }),
		btn("Choose files…", () => input.click(), "", { disabled: !running }), input);
	drop.addEventListener("dragover", (ev) => { if (!running) return; ev.preventDefault(); drop.dataset.over = "1"; });
	drop.addEventListener("dragleave", () => { drop.dataset.over = "0"; });
	drop.addEventListener("drop", (ev) => { ev.preventDefault(); drop.dataset.over = "0"; if (running) sendFiles([...ev.dataTransfer.files]); });
	const sendHelp = v86
		? [h("p", { class: "rv-lx-help", text: "Files you send land in the shared folder, mounted in the guest at " + GUEST_SHARE_MOUNT + " (done for you on a fresh Alpine boot). To mount it yourself:" }), codeLine(GUEST_MOUNT_COMMAND)]
		: [h("p", { class: "rv-lx-help", text: "Files you send appear in /data inside the machine (read-only there; copy them with cp /data/<name> ~/). Up to 64 MB each." })];
	const recvHelp = v86
		? h("p", { class: "rv-lx-help", text: "Everything in the shared folder (" + GUEST_SHARE_MOUNT + ") is listed here: save a file there from the guest, press Refresh, then Download." })
		: [h("p", { class: "rv-lx-help", text: "To download a file from the machine, export it in the terminal, then press Refresh:" }), codeLine("sh " + EXPORT_HELPER + " ~/myfile.txt")];
	const list = h("ul", { class: "rv-lx-list" });
	if (!running) list.append(h("li", { class: "rv-lx-empty", text: "Start the machine to see its files." }));
	else if (!S.exports) list.append(h("li", { class: "rv-lx-empty", text: "Loading…" }));
	else if (!S.exports.length) list.append(h("li", { class: "rv-lx-empty", text: "Nothing here yet." }));
	else for (const name of S.exports) list.append(h("li", { class: "rv-lx-row" }, h("span", { text: name }), btn("Download", () => downloadExport(name), "", { "aria-label": "Download " + name })));
	p.replaceChildren(h("div", { class: "rv-lx-scroll" },
		h("div", { class: "rv-lx-section" }, h("h2", { text: "Send to the machine" }), ...sendHelp, drop),
		h("div", { class: "rv-lx-section" }, h("h2", { text: "Files from the machine" }), recvHelp, list,
			h("div", { class: "rv-lx-buttons" }, btn("Refresh", refreshExports, "", { disabled: !running })))
	));
	if (running && !S.exports) refreshExports();
}

function codeLine(text) {
	return h("div", { class: "rv-lx-code" }, h("code", { text }), btn("Copy", async () => showNotice((await copyText(text)) ? "Copied." : "Couldn't copy; select the text instead.", "ok")));
}

async function sendFiles(files) {
	const e = S.engine;
	if (!e || e.state !== "running" || !files.length) return;
	const sent = [];
	for (const f of files) {
		try { sent.push(await e.importFile(f.name, f)); }
		catch (err) { showNotice(err && err.message ? err.message : String(err), "error"); return; }
	}
	showNotice("Sent: " + sent.join(", "), "ok");
	refreshExports();
}

async function refreshExports() {
	const e = S.engine;
	if (!e || e.state !== "running") { S.exports = null; if (S.tab === "files") renderFiles(); return; }
	try { S.exports = await e.listExports(); } catch (err) { S.exports = []; showNotice(err && err.message ? err.message : String(err), "error"); }
	if (S.tab === "files") renderFiles();
}

async function downloadExport(name) {
	const e = S.engine;
	if (!e) return;
	try {
		const blob = await e.exportFile(name);
		if (!blob) return showNotice(name + " is no longer there. Press Refresh.", "error");
		saveBlob(blob, name);
	} catch (err) { showNotice(err && err.message ? err.message : String(err), "error"); }
}

// --- Machine tab ----------------------------------------------------------------------------

async function renderMachine() {
	const p = els.panels.machine;
	const m = activeMachine();
	if (!m) { p.replaceChildren(h("div", { class: "rv-lx-scroll" }, h("p", { class: "rv-lx-help", text: "No machine selected. Press New to add one." }))); return; }
	const locked = !!S.engine && S.engineId === m.id && isRunning();
	S.uploads = await store.listImages().catch(() => []);
	const snap = m.engine === "v86" ? await store.getSnapshot(m.id).catch(() => null) : null;
	if (activeMachine() !== m) return; // switched while loading
	const draft = structuredClone(m);
	const name = h("input", { class: "rv-lx-input", id: "lx-name", value: m.name, maxlength: "80", disabled: locked });
	const form = h("div", { class: "rv-lx-form" }, h("label", { for: "lx-name", text: "Name" }), name);
	let image, memory, persist, network, autologin, acpi;
	if (m.engine === "v86") {
		image = h("select", { class: "rv-lx-select", id: "lx-image", disabled: locked });
		for (const img of LINUX_IMAGES) image.append(h("option", { value: "catalog:" + img.id, text: img.name + " (served by Re:Vault)" }));
		for (const u of S.uploads) image.append(h("option", { value: "upload:" + u.id, text: u.name + " (" + fmtBytes(u.size) + ", uploaded)" }));
		image.append(h("option", { value: "", text: "No disk (boot to BIOS)" }));
		image.value = m.image;
		memory = h("select", { class: "rv-lx-select", id: "lx-mem", disabled: locked });
		for (const mb of [128, 256, 384, 512, 768, 1024, 1536, 2048]) memory.append(h("option", { value: String(mb), text: mb + " MB" + (mb === 256 ? " (recommended)" : "") }));
		memory.value = String(m.memory);
		persist = h("input", { type: "checkbox", id: "lx-persist", checked: m.persist, disabled: locked });
		network = h("input", { type: "checkbox", id: "lx-net", checked: m.network !== false, disabled: locked });
		autologin = h("input", { type: "checkbox", id: "lx-auto", checked: m.autologin !== false, disabled: locked });
		acpi = h("input", { type: "checkbox", id: "lx-acpi", checked: !!m.acpi, disabled: locked });
		const upload = h("input", { type: "file", accept: ".iso,.img,.bin,.raw", hidden: true, onchange: () => { if (upload.files[0]) uploadImage(upload.files[0]); upload.value = ""; } });
		form.append(
			h("label", { for: "lx-image", text: "Disk image" }), h("div", null, image, " ", btn("Upload ISO/IMG…", () => upload.click(), "", { disabled: locked }), upload),
			h("small", { text: "Alpine is fetched once by Re:Vault's server (checksum-verified) and streamed to your browser. Uploaded images stay in this browser." }),
			h("label", { for: "lx-mem", text: "Memory" }), memory,
			h("small", { text: "More memory is slower to save and restore. 256 MB suits Chromebooks." }),
			h("label", { for: "lx-persist", text: "Save state when stopped" }), h("span", { class: "rv-lx-check" }, persist, h("span", { text: snap ? "Saved state: " + fmtBytes(snap.bytes) + ", " + new Date(snap.savedAt).toLocaleString() : "No saved state yet" })),
			h("label", { for: "lx-net", text: "Internet access" }), h("span", { class: "rv-lx-check" }, network, h("span", { text: networkNote() })),
			h("label", { for: "lx-auto", text: "Set up on boot" }), h("span", { class: "rv-lx-check" }, autologin, h("span", { text: "Log in as root, start the network, mount the shared folder" })),
			h("label", { for: "lx-acpi", text: "ACPI" }), h("span", { class: "rv-lx-check" }, acpi, h("span", { text: "Needed for Windows Vista, 7, 8 and 10. Newer Windows is very slow in this emulator and may not finish booting." }))
		);
	} else {
		form.append(
			h("label", { text: "Engine" }), h("span", { text: "CheerpX (x86 Linux by Leaning Technologies)" }),
			h("label", { text: "Disk" }), h("span", { text: m.desktop ? "Alpine desktop (WebVM image)" : "Debian (WebVM image)" }),
			h("small", { text: "Streamed from disks.webvm.io; your changes are kept in this browser. Networking uses Tailscale (your own account), not Re:Vault's relay." })
		);
	}
	const buttons = h("div", { class: "rv-lx-buttons" });
	const save = btn("Save changes", async () => {
		if (S.engineId === m.id && isRunning()) return showNotice("Stop the machine to change its settings.", "error");
		draft.name = name.value.trim().slice(0, 80) || m.name;
		if (m.engine === "v86") {
			draft.image = image.value; draft.memory = Number(memory.value); draft.persist = persist.checked; draft.network = network.checked; draft.autologin = autologin.checked; draft.acpi = acpi.checked;
			if (snap && (draft.image !== m.image || draft.memory !== m.memory || draft.acpi !== !!m.acpi) && !(await confirmDialog("Saved state won't fit", "This machine's saved state was taken with the old disk/memory, so it can't resume with the new setting. It is kept (not deleted) until you discard it.", "Save anyway"))) return;
		}
		try { await profiles.saveMachine(draft); reloadMachines(); showNotice("Saved.", "ok"); }
		catch (err) { showNotice(err.message, "error"); }
	}, "rv-btn-primary", { disabled: locked });
	buttons.append(save);
	if (m.engine === "v86" && snap) buttons.append(btn("Discard saved state", async () => {
		if (!(await confirmDialog("Discard saved state?", "The next start boots " + m.name + " fresh. Anything not saved in the shared folder or downloaded is lost.", "Discard", true))) return;
		await store.deleteSnapshot(m.id).catch((err) => showNotice(err.message, "error"));
		renderMachine();
		renderUsage();
	}, "rv-btn-danger", { disabled: locked }));
	if (m.engine === "cheerpx") buttons.append(btn("Reset disk…", () => resetCheerpx(m), "rv-btn-danger"));
	buttons.append(btn("Delete machine", () => deleteMachine(m), "rv-btn-danger", { disabled: locked }));
	p.replaceChildren(h("div", { class: "rv-lx-scroll" }, h("div", { class: "rv-lx-section" },
		h("h2", { text: "Machine" }),
		locked ? h("p", { class: "rv-lx-help", text: "Stop the machine to change its settings." }) : null,
		form, buttons
	)));
}

async function uploadImage(file) {
	const target = activeMachine();
	if (target && S.engineId === target.id && isRunning()) return showNotice("Stop the machine before changing its disk.", "error");
	const est = await store.estimate();
	if (!store.fitsInQuota(est, file.size)) return showNotice("Not enough browser storage for " + file.name + " (" + fmtBytes(file.size) + "). Free space in Settings > Advanced > Linux machines.", "error");
	const id = "u" + Date.now().toString(36);
	try {
		showNotice("Storing " + file.name + " in this browser…");
		await store.putImage(id, { blob: file, name: file.name.slice(0, 120), size: file.size, addedAt: Date.now() });
		const m = activeMachine();
		if (m && m.engine === "v86") await profiles.saveMachine({ ...m, image: "upload:" + id, autologin: false });
		reloadMachines();
		showNotice("Uploaded " + file.name + ". This machine now boots from it.", "ok");
	} catch (err) { showNotice("Couldn't store " + file.name + ": " + (err && err.message ? err.message : err), "error"); }
}

// A CheerpX delete/reset reloads this tab; refuse while ANOTHER machine runs here (it would be
// killed without saving). The machine itself is saved/stopped by the flows below.
function blockedByOther(m) {
	const r = runningMachine();
	if (r && r.id !== m.id) { showNotice("Stop " + r.name + " first: this reloads the Linux tab.", "error"); return true; }
	return false;
}

async function deleteMachine(m) {
	if (S.engineId === m.id && isRunning()) return showNotice("Stop " + m.name + " before deleting it.", "error");
	if (m.engine === "cheerpx" && blockedByOther(m)) return;
	if (!(await confirmDialog("Delete " + m.name + "?", m.engine === "v86" ? "Its saved state is deleted too. Uploaded images stay until you delete all Linux data in Settings." : "Its disk changes in this browser are deleted too (on the next page load).", "Delete", true))) return;
	await profiles.removeMachine(m.id);
	if (m.engine === "v86") await store.deleteSnapshot(m.id).catch(() => {});
	else { requestReset(m.id); }
	reloadMachines();
	if (m.engine === "cheerpx") location.reload();
}

async function resetCheerpx(m) {
	if (blockedByOther(m)) return;
	if (!(await confirmDialog("Reset " + m.name + "'s disk?", "This erases every change you made inside the machine (files, installed packages) and its exported files, then reloads this tab. It can't be undone.", "Reset disk and reload", true))) return;
	if (S.engine && S.engineId === m.id) { try { await S.engine.stop(); } catch (e) {} }
	requestReset(m.id);
	location.reload(); // the databases are deleted at the start of the next load, before CheerpX loads
}

// --- dialogs -------------------------------------------------------------------------------

async function newMachineDialog() {
	const choices = [
		["alpine", "Alpine Linux", "v86 · boots in about 20 seconds, works everywhere", () => profiles.v86Machine({ memory: Number(get("linuxMemory")) || 256 })],
		["custom", "Your own ISO or IMG", "v86 · upload it in the Machine tab", () => profiles.v86Machine({ name: "My machine", image: "", memory: Number(get("linuxMemory")) || 256, autologin: false })],
	];
	if (cheerpxEnabled()) {
		choices.push(["debian", "Debian terminal", "CheerpX · bash, apt, python", () => profiles.debianMachine()]);
		choices.push(["desktop", "Alpine desktop", "CheerpX · graphical desktop (i3)", () => profiles.desktopMachine()]);
		if (profiles.debianDesktopAvailable()) choices.push(["debian-desktop", "Debian desktop", "CheerpX · Debian 12 with an LXDE desktop, files, terminal, editor", () => profiles.debianDesktopMachine()]);
	}
	const pick = await modal((close) => [
		h("h2", { text: "New machine" }),
		h("ul", { class: "rv-lx-list" }, ...choices.map(([id, title, sub]) => h("li", { class: "rv-lx-row" }, h("span", null, h("b", { text: title }), " · ", sub), btn("Add", () => close(id), "rv-btn-primary", { "aria-label": "Add " + title })))),
		h("div", { class: "rv-lx-buttons" }, btn("Cancel", () => close(null))),
	], { label: "New machine" });
	const choice = choices.find((c) => c[0] === pick);
	if (!choice) return;
	const m = choice[3]();
	m.createdAt = Date.now();
	m.id = profiles.slugify(m.name, new Set(S.machines.map((x) => x.id)));
	try { await profiles.saveMachine(m); } catch (err) { return showNotice(err.message, "error"); }
	reloadMachines();
	await selectMachine(m.id);
	setTab(pick === "custom" ? "machine" : "console");
}

function licenceNotice() {
	return modal((close) => {
		const check = h("input", { type: "checkbox", id: "lx-lic" });
		const go = btn("Continue", () => close(true), "rv-btn-primary", { disabled: true });
		check.addEventListener("change", () => { go.disabled = !check.checked; });
		return [
			h("h2", { text: NOTICE_HEADING }),
			h("p", { text: NOTICE_TEXT }),
			h("ul", null, ...NOTICE_LINKS.map(([label, href]) => h("li", null, h("a", { href, target: "_blank", rel: "noopener noreferrer", text: label })))),
			h("label", { class: "rv-lx-check", for: "lx-lic" }, check, h("span", { text: NOTICE_CHECKBOX })),
			h("div", { class: "rv-lx-buttons" }, go, btn("Cancel", () => close(false))),
		];
	}, { label: NOTICE_HEADING }).then((v) => v === true);
}

function licencesDialog() {
	const f = (name, label) => h("li", null, h("a", { href: "/licenses/linux/" + name, target: "_blank", rel: "noopener", text: label }));
	return modal((close) => [
		h("h2", { text: "Licences" }),
		h("p", { text: "Re:Vault Linux ships these open-source components:" }),
		h("ul", null,
			f("v86-BSD-2-Clause.txt", "v86 PC emulator (BSD-2-Clause)"),
			f("SeaBIOS-LGPL-3.0.txt", "SeaBIOS (LGPL-3.0)"),
			f("VGABIOS-LGPL-2.1.txt", "Bochs/Plex86 VGABIOS (LGPL-2.1)"),
			f("xterm.js-MIT.txt", "xterm.js (MIT)")),
		h("p", { text: "Alpine Linux is freely redistributable; its packages carry their own licences. CheerpX is loaded from Leaning Technologies' servers under the CheerpX Community License and is not redistributed by Re:Vault. " + ATTRIBUTION }),
		h("div", { class: "rv-lx-buttons" }, btn("Close", () => close(), "rv-btn-primary")),
	], { label: "Licences" });
}

// --- boot ---------------------------------------------------------------------------------

function reloadMachines() {
	S.machines = profiles.listMachines();
	const vis = visibleMachines();
	if (!vis.some((m) => m.id === S.activeId)) S.activeId = (vis[0] && vis[0].id) || null;
	renderSidebar(); renderBar();
	if (S.tab === "machine") renderMachine();
	renderUsage();
}

async function renderUsage() {
	try {
		const u = await store.usage();
		els.usage.textContent = "Stored here: " + fmtBytes(u.snapshots + u.images) + (u.cheerpxDisks ? " + " + u.cheerpxDisks / 2 + " CheerpX disk" + (u.cheerpxDisks > 2 ? "s" : "") : "");
	} catch (e) { els.usage.textContent = ""; }
}

// First machine of the preferred engine (Settings > Advanced > Linux machines > Default engine), else the first one.
function defaultMachineId() {
	const vis = visibleMachines();
	const want = get("linuxEngine") === "cheerpx" && cheerpxEnabled() ? "cheerpx" : "v86";
	return ((vis.find((m) => m.engine === want) || vis[0]) || {}).id || null;
}

function consumeRestart() {
	try {
		const r = JSON.parse(sessionStorage.getItem(RESTART_KEY) || "null");
		sessionStorage.removeItem(RESTART_KEY);
		return r && typeof r.id === "string" && Date.now() - r.at < AUTOBOOT_TTL ? r.id : null;
	} catch (e) { return null; }
}

function consumeAutoboot() {
	try {
		const at = Number(sessionStorage.getItem(AUTOBOOT_KEY));
		sessionStorage.removeItem(AUTOBOOT_KEY);
		return at > 0 && Date.now() - at < AUTOBOOT_TTL;
	} catch (e) { return false; }
}

async function boot() {
	// A pending CheerpX reset must run before anything can load CheerpX in this document.
	let resetResult = null;
	try { resetResult = await consumePendingReset(); } catch (e) {}
	build();
	await profiles.ensureDefaults(Number(get("linuxMemory")) || profiles.DEFAULT_MEMORY).catch(() => {});
	S.machines = profiles.listMachines();
	const saved = profiles.getActiveId();
	S.activeId = visibleMachines().some((m) => m.id === saved) ? saved : defaultMachineId();
	setTab("console");
	renderSidebar(); renderBar(); renderUsage();
	term.writeln("Re:Vault Linux. Pick a machine and press Start.\n");
	if (resetResult) showNotice(resetResult.ok ? "The disk was reset." : "The disk wasn't reset: that machine is open in another Re:Vault tab. Stop it or close that tab, then choose Reset disk again.", resetResult.ok ? "ok" : "error");
	if (!__RVM.linux) showNotice("This Re:Vault server needs a rebuild before Linux can run (missing disk route).", "error");

	let lastEngine = get("linuxEngine");
	onSettings(() => {
		reloadMachines();
		term.applyTheme();
		// Changing the default engine picks that kind of machine (unless one is running).
		if (get("linuxEngine") !== lastEngine) { lastEngine = get("linuxEngine"); if (!isRunning()) { const id = defaultMachineId(); if (id) selectMachine(id); } }
	});
	// Lets the app ask before closing this tab with a machine running.
	window.revaultLinuxBusy = () => { const m = runningMachine(); return m ? m.name : null; };
	// Read-only: the whole terminal buffer as text (the live test reads output that has scrolled).
	window.revaultLinuxConsoleText = () => term.text();
	window.addEventListener("pagehide", () => { S.disposed = true; if (S.starting) S.starting.abort(); disposeEngine(); });

	const restart = consumeRestart();
	if (restart && S.machines.some((m) => m.id === restart)) { await selectMachine(restart); startMachine(activeMachine()); }
	else if (consumeAutoboot()) {
		const m = activeMachine();
		if (m) startMachine(m);
	}
}

boot();
